import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useSocket } from '../hooks/useSocket'

interface ExpressSlot {
  slot_key: string
  slot_start: string
  slot_end: string
  label: string
  current_workload_score: number
  max_workload_score: number
  is_full: boolean
  order_count: number
  is_disabled: boolean
  orders: ExpressOrder[]
}

interface ExpressOrder {
  order_id: string
  order_number: string
  customer_name: string
  status: string
  workload_score: number
}

const formatSlotTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

const getCapacityPercent = (slot: ExpressSlot) =>
  slot.max_workload_score > 0
    ? Math.round((slot.current_workload_score / slot.max_workload_score) * 100)
    : 0

const getCapacityBarColor = (percent: number) => {
  if (percent >= 100) return '#ef4444'
  if (percent >= 75) return '#f59e0b'
  return '#22c55e'
}

const getSlotBadge = (slot: ExpressSlot): { label: string; bg: string; color: string } => {
  if (slot.is_disabled) return { label: 'Disabled', bg: '#f3f4f6', color: '#6b7280' }
  const pct = getCapacityPercent(slot)
  if (pct >= 100) return { label: 'Full', bg: '#fef2f2', color: '#dc2626' }
  if (pct >= 75) return { label: 'Almost Full', bg: '#fefce8', color: '#854d0e' }
  return { label: 'Open', bg: '#f0fdf4', color: '#15803d' }
}

const getOrderStatusStyle = (status: string): { bg: string; color: string } => {
  switch (status) {
    case 'placed':
    case 'confirmed':
      return { bg: '#eff6ff', color: '#1d4ed8' }
    case 'agent_assigned':
    case 'en_route_pickup':
    case 'picked_up':
    case 'out_for_delivery':
      return { bg: '#f0fdf4', color: '#15803d' }
    case 'at_facility':
    case 'in_iron':
    case 'in_wash':
    case 'ready':
      return { bg: '#fefce8', color: '#854d0e' }
    default:
      return { bg: '#f3f4f6', color: '#6b7280' }
  }
}

const toLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

export default function ExpressOrdersPage() {
  const colors = useColors()
  const [slots, setSlots] = useState<ExpressSlot[]>([])
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set())

  const loadSlots = useCallback(async () => {
    try {
      const res = await adminApi.getExpressSlots()
      const data = res?.data ?? res
      setSlots(Array.isArray(data?.slots) ? data.slots : [])
    } catch {
      /* ignore */
    }
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      await loadSlots()
    } finally {
      setLoading(false)
    }
  }, [loadSlots])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  // Auto-refresh every 30s
  useEffect(() => {
    if (!autoRefresh) return
    const id = setInterval(() => {
      void loadSlots()
    }, 30_000)
    return () => clearInterval(id)
  }, [autoRefresh, loadSlots])

  // Socket real-time updates
  const { socket, status } = useSocket({
    'express_slot:updated': () => {
      void loadSlots()
    },
    'express_slot:full': () => {
      void loadSlots()
    },
    'express_slot:available': () => {
      void loadSlots()
    },
    'order:status_changed': () => {
      void loadSlots()
    },
    'order:created': () => {
      void loadSlots()
    },
  })

  useEffect(() => {
    if (!socket) {
      console.warn('[ExpressOrders] socket not ready. Relying on the 30s poll for slot updates')
      return
    }
    // Re-subscribe whenever the socket (re)connects so a reconnect re-joins the
    // express room (the live page would otherwise stop updating after a drop).
    if (status !== 'connected') return
    socket.emit('subscribe:express_slots')
    return () => {
      socket.emit('unsubscribe:express_slots')
    }
  }, [socket, status])

  const ordersBySlot = useMemo(() => {
    const map: Record<string, ExpressOrder[]> = {}
    for (const slot of slots) {
      map[slot.slot_key] = Array.isArray(slot.orders) ? slot.orders : []
    }
    return map
  }, [slots])

  const nextSixSlots = useMemo(
    () =>
      [...slots]
        .sort((a, b) => new Date(a.slot_start).getTime() - new Date(b.slot_start).getTime())
        .slice(0, 6),
    [slots]
  )

  const slotsWithOrders = useMemo(
    () => slots.filter((slot) => slot.orders.length > 0),
    [slots]
  )

  const toggleSlot = (slotId: string) => {
    setExpandedSlots((prev) => {
      const next = new Set(prev)
      if (next.has(slotId)) next.delete(slotId)
      else next.add(slotId)
      return next
    })
  }

  const handleToggleDisable = async (slot: ExpressSlot) => {
    try {
      if (slot.is_disabled) {
        await adminApi.enableExpressSlot(encodeURIComponent(slot.slot_key))
      } else {
        await adminApi.disableExpressSlot(encodeURIComponent(slot.slot_key))
      }
      await loadSlots()
    } catch {
      /* ignore */
    }
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          padding: '24px 32px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h2
            className="font-display"
            style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: colors.textPrimary }}
          >
            Express Orders · Express Steam Iron
          </h2>
          <p
            className="font-body"
            style={{ margin: '4px 0 0', fontSize: '13px', color: colors.textSecondary }}
          >
            Live view of all active express slots
          </p>
        </div>
        <label
          className="font-body"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            color: colors.textSecondary,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (30s)
        </label>
      </div>

      {/* Active Slots Panel */}
      <div style={{ margin: '20px 32px' }}>
        {loading ? (
          <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
            Loading express slots...
          </p>
        ) : nextSixSlots.length === 0 ? (
          <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
            No active express slots
          </p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '16px',
            }}
          >
            {nextSixSlots.map((slot) => {
              const pct = getCapacityPercent(slot)
              const badge = getSlotBadge(slot)
              return (
                <div
                  key={slot.slot_key}
                  style={{
                    background: colors.bgCard,
                    borderRadius: '14px',
                    padding: '20px',
                    border: `1.5px solid ${colors.border}`,
                  }}
                >
                  <p
                    className="font-body"
                    style={{
                      margin: '0 0 10px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: colors.textPrimary,
                    }}
                  >
                    {formatSlotTime(slot.slot_start)} – {formatSlotTime(slot.slot_end)}
                  </p>

                  {/* Capacity bar */}
                  <div
                    style={{
                      height: '8px',
                      borderRadius: '4px',
                      background: colors.bgSecondary,
                      overflow: 'hidden',
                      marginBottom: '8px',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(pct, 100)}%`,
                        height: '100%',
                        borderRadius: '4px',
                        background: getCapacityBarColor(pct),
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>

                  <p
                    className="font-body"
                    style={{ margin: '0 0 4px', fontSize: '12px', color: colors.textSecondary }}
                  >
                    {slot.current_workload_score}/{slot.max_workload_score} workload
                  </p>

                  <p
                    className="font-body"
                    style={{
                      margin: '0 0 10px',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: colors.textPrimary,
                    }}
                  >
                    {slot.order_count} orders
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span
                      className="font-body"
                      style={{
                        borderRadius: '999px',
                        padding: '4px 10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: badge.bg,
                        color: badge.color,
                      }}
                    >
                      {badge.label}
                    </span>

                    <button
                      type="button"
                      className="font-body"
                      onClick={() => void handleToggleDisable(slot)}
                      style={{
                        fontSize: '12px',
                        borderRadius: '8px',
                        border: `1px solid ${colors.border}`,
                        background: 'transparent',
                        color: colors.textSecondary,
                        padding: '4px 10px',
                        cursor: 'pointer',
                      }}
                    >
                      {slot.is_disabled ? 'Enable Slot' : 'Disable Slot'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Orders by Slot */}
      <div style={{ padding: '0 32px 24px' }}>
        <h3
          className="font-display"
          style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}
        >
          Orders by Slot
        </h3>

        {loading ? (
          <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
            Loading express orders...
          </p>
        ) : slotsWithOrders.length === 0 ? (
          <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
            No express orders
          </p>
        ) : (
          slotsWithOrders.map((slot) => {
            const slotOrders = ordersBySlot[slot.slot_key] ?? []
            const isExpanded = expandedSlots.has(slot.slot_key)

            return (
              <div
                key={slot.slot_key}
                style={{
                  marginBottom: '12px',
                  background: colors.bgCard,
                  borderRadius: '16px',
                  boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                }}
              >
                {/* Group header */}
                <div
                  onClick={() => toggleSlot(slot.slot_key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '14px 18px',
                    cursor: 'pointer',
                    borderBottom: isExpanded ? `1px solid ${colors.border}` : 'none',
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown size={16} color={colors.textSecondary} />
                  ) : (
                    <ChevronRight size={16} color={colors.textSecondary} />
                  )}
                  <span
                    className="font-body"
                    style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}
                  >
                    {formatSlotTime(slot.slot_start)} – {formatSlotTime(slot.slot_end)}
                  </span>
                  <span
                    className="font-body"
                    style={{ fontSize: '12px', color: colors.textSecondary }}
                  >
                    {slotOrders.length} order{slotOrders.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Expanded table */}
                {isExpanded && (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr
                        style={{
                          background: colors.bgSecondary,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        {['Order #', 'Customer', 'Workload', 'Status', 'Action'].map(
                          (header) => (
                            <th
                              key={header}
                              className="font-body"
                              style={{
                                padding: '14px 18px',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: colors.textMuted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                textAlign: 'left',
                              }}
                            >
                              {header}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {slotOrders.map((order) => {
                        const statusStyle = getOrderStatusStyle(order.status)
                        return (
                          <tr key={order.order_id} style={{ borderBottom: `0.5px solid ${colors.border}` }}>
                            <td
                              className="font-body"
                              style={{
                                padding: '14px 18px',
                                fontSize: '13px',
                                fontWeight: 600,
                                color: colors.textPrimary,
                              }}
                            >
                              {order.order_number}
                            </td>
                            <td
                              className="font-body"
                              style={{ padding: '14px 18px', fontSize: '13px', color: colors.textPrimary }}
                            >
                              {order.customer_name}
                            </td>
                            <td
                              className="font-body"
                              style={{ padding: '14px 18px', fontSize: '13px', color: colors.textPrimary }}
                            >
                              {order.workload_score}
                            </td>
                            <td style={{ padding: '14px 18px' }}>
                              <span
                                className="font-body"
                                style={{
                                  background: statusStyle.bg,
                                  color: statusStyle.color,
                                  borderRadius: '999px',
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                }}
                              >
                                {toLabel(order.status)}
                              </span>
                            </td>
                            <td style={{ padding: '14px 18px' }}>
                              <span
                                className="font-body"
                                onClick={() => {
                                  window.location.href = `/orders?id=${order.order_id}`
                                }}
                                style={{
                                  color: colors.accent,
                                  fontWeight: 600,
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                }}
                              >
                                View
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
